import Apartment from "@mui/icons-material/Apartment";
import AssignmentInd from "@mui/icons-material/AssignmentInd";
import Autorenew from "@mui/icons-material/Autorenew";
import AutoStories from "@mui/icons-material/AutoStories";
import BarChart from "@mui/icons-material/BarChart";
import Chat from "@mui/icons-material/Chat";
import ContentCopy from "@mui/icons-material/ContentCopy";
import CurrencyRupeeSharp from "@mui/icons-material/CurrencyRupeeSharp";
import DomainDisabled from "@mui/icons-material/DomainDisabled";
import EmojiEvents from "@mui/icons-material/EmojiEvents";
import Equalizer from "@mui/icons-material/Equalizer";
import PersonOff from "@mui/icons-material/PersonOff";
import Event from "@mui/icons-material/Event";
import FactCheck from "@mui/icons-material/FactCheck";
import Groups from "@mui/icons-material/Groups";
import InsertChart from "@mui/icons-material/InsertChart";
import LeaderboardSharp from "@mui/icons-material/LeaderboardSharp";
import Lightbulb from "@mui/icons-material/Lightbulb";
import LinkIcon from "@mui/icons-material/Link";
import MenuBook from "@mui/icons-material/MenuBook";
import OpenInNew from "@mui/icons-material/OpenInNew";
import PersonSharp from "@mui/icons-material/PersonSharp";
import Queue from "@mui/icons-material/Queue";
import RateReviewOutlined from "@mui/icons-material/RateReviewOutlined";
import StackedBarChart from "@mui/icons-material/StackedBarChart";
import ThumbUp from "@mui/icons-material/ThumbUp";
import TrendingUp from "@mui/icons-material/TrendingUp";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";

import Select from "react-select";
import moment from "moment";

import {
  Alert,
  Box,
  Card,
  Chip,
  Container,
  Typography,
  Tooltip,
  IconButton,
  Stack,
  Button,
  Skeleton
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  fetchallcitylist,
  fetchcityambassadorbyid,
  fetchdashboardcounter
} from "src/utils/common";
import { notification } from "src/utils/messages";

// components
import Page from "../components/Page";
import { useNavigate } from "react-router-dom";
import { Routes } from "src/constant/api_url";

// ----------------------------------------------------------------------

// Design tokens (LVB Privilege design system)
const NAVY = "#003366";
const DARK_NAVY = "#001e40";
const ON_SURFACE = "#0b1c30";
const ON_SURFACE_VARIANT = "#43474f";
const SECONDARY_TEXT = "#5c5f61";
const SURFACE = "#f8f9ff";
const SURFACE_CONTAINER = "#e5eeff";
const OUTLINE = "#737780";
const OUTLINE_VARIANT = "#c3c6d1";
const AMBER = "#fbbf24";
const AMBER_DARK = "#b45309";
const GREEN = "#22c55e";
const RED = "#ef4444";
const ORANGE = "#f97316";
const BLUE = "#3b82f6";
const YELLOW = "#eab308";
const PUBLIC_SANS = "'Public Sans', sans-serif";

const cardSx = {
  borderRadius: 3,
  border: "1px solid rgba(255, 255, 255, 0.6)",
  boxShadow: "0px 10px 30px rgba(0, 51, 102, 0.05)",
  background: "#ffffff",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0px 16px 40px rgba(0, 51, 102, 0.12)"
  }
};

// ----------------------------------------------------------------------

function MetricIcon({ tone, size = 48, children }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: tone,
        background: "linear-gradient(135deg, #f8f9ff 0%, #e5eeff 100%)"
      }}
    >
      {children}
    </Box>
  );
}

function SectionHeader({ icon, title }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
      <Box sx={{ color: NAVY, display: "flex", alignItems: "center" }}>{icon}</Box>
      <Typography
        sx={{
          fontFamily: PUBLIC_SANS,
          fontWeight: 600,
          fontSize: 18,
          color: ON_SURFACE_VARIANT
        }}
      >
        {title}
      </Typography>
    </Stack>
  );
}

function MetricTile({ accent, tone, icon, value, label, onClick }) {
  return (
    <Card
      onClick={onClick}
      sx={{
        ...cardSx,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        p: 3,
        cursor: onClick ? "pointer" : "default"
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: 4,
          bgcolor: accent
        }}
      />
      <MetricIcon tone={tone}>{icon}</MetricIcon>
      <Typography
        sx={{
          fontFamily: PUBLIC_SANS,
          fontWeight: 800,
          fontSize: 32,
          lineHeight: 1,
          letterSpacing: "-0.01em",
          color: ON_SURFACE,
          mt: 2,
          mb: 1
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          fontFamily: PUBLIC_SANS,
          fontWeight: 600,
          fontSize: 13,
          letterSpacing: "0.03em",
          color: SECONDARY_TEXT
        }}
      >
        {label}
      </Typography>
    </Card>
  );
}

function ApprovalTile({ tone, icon, imageSrc, title, today, pending, onClick }) {
  return (
    <Card
      onClick={onClick}
      sx={{
        ...cardSx,
        p: 2.5,
        display: "flex",
        flexDirection: "column",
        cursor: onClick ? "pointer" : "default"
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <Box sx={{ color: tone, display: "flex", alignItems: "center" }}>
          {imageSrc ? (
            <img src={imageSrc} alt={title} style={{ width: 22, height: 22, objectFit: "contain" }} />
          ) : icon}
        </Box>
        <Typography sx={{ fontFamily: PUBLIC_SANS, fontWeight: 700, fontSize: 13, color: SECONDARY_TEXT }}>
          {title}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="flex-end" justifyContent="space-between" spacing={2} mt="auto">
        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{ fontFamily: PUBLIC_SANS, fontWeight: 600, fontSize: 22, color: ON_SURFACE }}>
            {today}
          </Typography>
          <Typography sx={{ fontFamily: PUBLIC_SANS, fontSize: 12, color: OUTLINE }}>Today</Typography>
        </Box>
        {/* <Box sx={{ width: 1, height: 24, bgcolor: OUTLINE_VARIANT }} /> */}
        <Box sx={{ textAlign: "center" }}>
          <Typography
            sx={{
              fontFamily: PUBLIC_SANS,
              fontWeight: 600,
              fontSize: 22,
              color: pending > 0 ? RED : ON_SURFACE
            }}
          >
            {pending}
          </Typography>
          <Typography sx={{ fontFamily: PUBLIC_SANS, fontSize: 12, color: OUTLINE }}>Pending</Typography>
        </Box>
      </Stack>
    </Card>
  );
}

function QueueCard({ tone, icon, imageSrc, value, label, onClick }) {
  return (
    <Card
      onClick={onClick}
      sx={{
        ...cardSx,
        p: 3,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        cursor: onClick ? "pointer" : "default",
        "&:hover": {
          ...cardSx["&:hover"],
          bgcolor: "#eff4ff"
        }
      }}
    >
      <Box sx={{ color: tone, display: "flex", alignItems: "center", mb: 1 }}>
        {imageSrc ? (
          <img src={imageSrc} alt={label} style={{ width: 28, height: 28, objectFit: "contain" }} />
        ) : icon}
      </Box>
      <Typography
        sx={{
          fontFamily: PUBLIC_SANS,
          fontWeight: 800,
          fontSize: 32,
          lineHeight: 1,
          color: ON_SURFACE,
          mb: 1
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          fontFamily: PUBLIC_SANS,
          fontSize: 13,
          fontWeight: 600,
          color: SECONDARY_TEXT,
          lineHeight: 1.4
        }}
      >
        {label}
      </Typography>
    </Card>
  );
}

function FeedbackTile({ tone, icon, title, today, pending, onClick }) {
  return (
    <Card
      onClick={onClick}
      sx={{
        ...cardSx,
        p: 2.5,
        display: "flex",
        flexDirection: "column",
        cursor: onClick ? "pointer" : "default"
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <Box sx={{ color: tone, display: "flex", alignItems: "center" }}>{icon}</Box>
        <Typography
          sx={{ fontFamily: PUBLIC_SANS, fontWeight: 700, fontSize: 13, color: SECONDARY_TEXT }}
        >
          {title}
        </Typography>
      </Stack>
      <Box sx={{ mt: "auto" }}>
        <Typography
          sx={{
            fontFamily: PUBLIC_SANS,
            fontWeight: 800,
            fontSize: 28,
            lineHeight: 1,
            color: ON_SURFACE,
            mb: 0.5
          }}
        >
          {today} / <span style={{ color: pending > 0 ? RED : ON_SURFACE }}>{pending}</span>
        </Typography>
        <Typography sx={{ fontFamily: PUBLIC_SANS, fontSize: 12, color: OUTLINE }}>
          Today / Pending
        </Typography>
      </Box>
    </Card>
  );
}

function MeetingStatsCard({ meetingToday, meetingClosePending, onClick }) {
  return (
    <Card
      onClick={onClick}
      sx={{
        ...cardSx,
        p: 2.5,
        display: "flex",
        flexDirection: "column",
        cursor: onClick ? "pointer" : "default"
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <Box sx={{ color: GREEN, display: "flex", alignItems: "center" }}>
          <Event sx={{ fontSize: 22 }} />
        </Box>
        <Typography
          sx={{ fontFamily: PUBLIC_SANS, fontWeight: 700, fontSize: 13, color: SECONDARY_TEXT }}
        >
          Meetings
        </Typography>
      </Stack>
      <Box sx={{ mt: "auto" }}>
        <Typography
          sx={{
            fontFamily: PUBLIC_SANS,
            fontWeight: 800,
            fontSize: 28,
            lineHeight: 1,
            color: ON_SURFACE,
            mb: 0.5
          }}
        >
          {meetingToday} / <span style={{ color: meetingClosePending > 0 ? RED : ON_SURFACE }}>{meetingClosePending}</span>
        </Typography>
        <Typography sx={{ fontFamily: PUBLIC_SANS, fontSize: 12, color: OUTLINE }}>
          Today / Close Pending
        </Typography>
      </Box>
    </Card>
  );
}

function PinEligibleCard({ imageUrl, name, counter, onClick }) {
  return (
    <Card
      onClick={onClick}
      sx={{
        ...cardSx,
        p: 3,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        cursor: onClick ? "pointer" : "default"
      }}
    >
      <Box
        component="img"
        src={imageUrl}
        alt={name}
        sx={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          objectFit: "cover",
          mb: 1.5,
        }}
      />
      <Typography
        sx={{
          fontFamily: PUBLIC_SANS,
          fontWeight: 800,
          fontSize: 32,
          lineHeight: 1,
          color: ON_SURFACE,
          mb: 1
        }}
      >
        {counter}
      </Typography>
      <Typography
        sx={{
          fontFamily: PUBLIC_SANS,
          fontSize: 13,
          fontWeight: 600,
          color: SECONDARY_TEXT,
          lineHeight: 1.4
        }}
      >
        {name}
      </Typography>
    </Card>
  );
}

// One clickable counter row, shared by every card in the Membership & Chapters group.
function StatRow({ icon, tint, color, label, sub, value, onClick, divider }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        py: 1.25,
        mt: divider ? 1.5 : 0,
        borderTop: divider ? `1px solid ${OUTLINE_VARIANT}` : "none",
        cursor: "pointer",
        borderRadius: 1,
        transition: "background 0.2s ease",
        "&:hover": { bgcolor: SURFACE }
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            bgcolor: tint,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color,
            flexShrink: 0
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: PUBLIC_SANS,
              fontSize: 13,
              fontWeight: 600,
              color: SECONDARY_TEXT,
              whiteSpace: { xs: "normal", lg: "nowrap" }
            }}
          >
            {label}
          </Typography>
          {sub && (
            <Typography
              sx={{
                fontFamily: PUBLIC_SANS,
                fontSize: 11,
                color: OUTLINE
              }}
            >
              {sub}
            </Typography>
          )}
        </Box>
      </Stack>
      <Typography
        sx={{
          fontFamily: PUBLIC_SANS,
          fontWeight: 700,
          fontSize: 22,
          color: ON_SURFACE,
          flexShrink: 0
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

// ----------------------------------------------------------------------
export default function DashboardApp() {
  let loginUser = JSON.parse(localStorage.getItem("userData"));
  let navigate = useNavigate();
  const [formLink, setFormLink] = useState();
  const [dashboardCount, setDashboardCount] = useState({});
  const [loading, setLoading] = useState(true);
  const [citylist, setcitylist] = useState([]);
  const [filtereddata, setFilteredData] = useState({
    city: loginUser?.city_id || 0
  });

  const isAdmin = loginUser?.u_type == 0;
  const isAmbassador = loginUser?.u_type == 1;
  const isStaff = loginUser?.u_type == 2;
  const showCitySelect = isAdmin;
  const showApprovals = !isStaff;
  const showFeedback =
    !isStaff ||
    (isStaff && loginUser?.management_p != 0) ||
    (isStaff && loginUser?.is_subadmin == 1);

  useEffect(async () => {
    setLoading(true);
    setDashboardCount(
      await fetchdashboardcounter(loginUser?.u_id, loginUser?.u_type, filtereddata?.city)
    );
    if (loginUser?.u_type == 1 || (loginUser?.u_type == 2 && loginUser?.p_user_id != 1)) {
      setCityFormLink();
    } else {
      setFormLink(window.location.origin + "/inquiry-form");
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtereddata]);

  const setCityFormLink = async () => {
    const resp = await fetchcityambassadorbyid(loginUser?.city_id);
    let data = resp && resp.u_id.toString();
    let buff = new Buffer(data);
    let base64data = buff.toString("base64");
    setFormLink(
      Routes.LEADFORMPUBLICURL + "lvb-membership-form/" + resp.city_name + "/" + base64data
    );
  };

  const copyFormLink = async () => {
    navigator.clipboard.writeText(formLink);
    let notify = notification({
      type: "info",
      message: "Copy to clipboard",
      position: "top"
    });
    await notify();
  };

  const openFormInNewTab = () => {
    if (formLink) {
      window.open(formLink, "_blank");
    }
  };

  const getallcitylist = async () => {
    setcitylist(await fetchallcitylist());
  };

  useEffect(() => {
    getallcitylist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Page title="Local Vocal Business Group">
      <Box sx={{ minHeight: "100vh" }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              flexWrap: "wrap",
              gap: 2,
              mb: 3
            }}
          >
            <Typography
              sx={{
                fontFamily: PUBLIC_SANS,
                fontWeight: 600,
                fontSize: { xs: 22, md: 28 },
                color: DARK_NAVY
              }}
            >
              Hi, Welcome {loginUser?.name}
            </Typography>

            {showCitySelect && (
              <Stack spacing={1} alignItems="flex-end">
                <Typography
                  sx={{
                    fontFamily: PUBLIC_SANS,
                    fontSize: 13,
                    fontWeight: 600,
                    color: ON_SURFACE_VARIANT,
                    letterSpacing: "0.03em",
                    textTransform: "uppercase"
                  }}
                >
                  Select City
                </Typography>
                <Select
                  value={
                    filtereddata.city == 0
                      ? {
                        value: 0,
                        label: "All City"
                      }
                      : citylist.map((city) => {
                        return (
                          filtereddata.city == city.city_id && {
                            value: city.city_id,
                            label: city.city_name
                          }
                        );
                      })
                  }
                  onChange={(e) => {
                    setFilteredData({
                      ...filtereddata,
                      city: e.value
                    });
                  }}
                  options={[
                    {
                      value: 0,
                      label: "All City"
                    },
                    ...citylist.map((city) => {
                      return {
                        label: city?.city_name,
                        value: city?.city_id
                      };
                    })
                  ]}
                  styles={{
                    control: (base) => ({
                      ...base,
                      width: "200px",
                      border: `1px solid ${OUTLINE_VARIANT}`,
                      borderRadius: 8,
                      boxShadow: "none",
                      "&:hover": { borderColor: NAVY }
                    }),
                    placeholder: (base) => ({
                      ...base,
                      fontFamily: PUBLIC_SANS,
                      fontWeight: 600,
                      fontSize: 13
                    }),
                    singleValue: (base) => ({
                      ...base,
                      fontFamily: PUBLIC_SANS,
                      fontWeight: 600,
                      fontSize: 13
                    }),
                    option: (base) => ({
                      ...base,
                      fontFamily: PUBLIC_SANS,
                      fontWeight: 600,
                      fontSize: 13,
                    }),
                    indicatorSeparator: (base) => ({ ...base, display: "none" })
                  }}
                />
              </Stack>
            )}
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {/* Form Link skeleton */}
              <Card sx={{ ...cardSx, p: 2.5, mb: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width={120} height={28} />
                    <Skeleton variant="rounded" width="60%" height={36} sx={{ mt: 1, borderRadius: 1 }} />
                  </Box>
                  <Skeleton variant="rounded" width={120} height={40} sx={{ borderRadius: 2 }} />
                </Box>
              </Card>

              {/* Lead Management skeleton */}
              <Box>
                <Skeleton variant="text" width={200} height={32} sx={{ mb: 2 }} />
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 3 }}>
                  {[...Array(4)].map((_, i) => (
                    <Card key={i} sx={{ ...cardSx, p: 3 }}>
                      <Skeleton variant="circular" width={44} height={44} sx={{ mb: 2 }} />
                      <Skeleton variant="text" width={60} height={36} />
                      <Skeleton variant="text" width="80%" height={20} />
                    </Card>
                  ))}
                </Box>
              </Box>

              {/* Membership & Chapters skeleton */}
              <Box>
                <Skeleton variant="text" width={240} height={32} sx={{ mb: 2 }} />
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr", lg: "repeat(5, 1fr)" }, gap: 3 }}>
                  <Card sx={{ ...cardSx, gridColumn: { lg: "span 2" }, p: 3, display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Skeleton variant="circular" width={56} height={56} />
                    <Skeleton variant="text" width={80} height={40} sx={{ mt: 2 }} />
                    <Skeleton variant="text" width={120} height={20} />
                  </Card>
                  <Card sx={{ ...cardSx, p: 2 }}>
                    <Skeleton variant="text" width="70%" height={28} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width={40} height={28} />
                    <Skeleton variant="text" width="70%" height={28} sx={{ mt: 1 }} />
                    <Skeleton variant="text" width={40} height={28} />
                  </Card>
                  <Card sx={{ ...cardSx, gridColumn: { lg: "span 2" }, p: 3, display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Skeleton variant="circular" width={56} height={56} />
                    <Skeleton variant="text" width={80} height={40} sx={{ mt: 2 }} />
                    <Skeleton variant="text" width={140} height={20} />
                  </Card>
                </Box>
              </Box>

              {/* Approvals + Pending Queue skeleton */}
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3 }}>
                <Box>
                  <Skeleton variant="text" width={180} height={32} sx={{ mb: 2 }} />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" }, gap: 3 }}>
                    {[...Array(3)].map((_, i) => (
                      <Card key={i} sx={{ ...cardSx, p: 2.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                          <Skeleton variant="circular" width={40} height={40} />
                          <Skeleton variant="text" width="60%" height={24} />
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Skeleton variant="text" width={60} height={20} />
                          <Skeleton variant="text" width={60} height={20} />
                        </Box>
                      </Card>
                    ))}
                  </Box>
                </Box>
                <Box>
                  <Skeleton variant="text" width={220} height={32} sx={{ mb: 2 }} />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" }, gap: 3 }}>
                    {[...Array(3)].map((_, i) => (
                      <Card key={i} sx={{ ...cardSx, p: 2.5 }}>
                        <Skeleton variant="circular" width={40} height={40} sx={{ mb: 2 }} />
                        <Skeleton variant="text" width="70%" height={32} />
                        <Skeleton variant="text" width="90%" height={20} />
                      </Card>
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* Activity Overview skeleton */}
              <Box>
                <Skeleton variant="text" width={200} height={32} sx={{ mb: 2 }} />
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 3 }}>
                  {[...Array(4)].map((_, i) => (
                    <Card key={i} sx={{ ...cardSx, p: 2.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <Skeleton variant="circular" width={36} height={36} />
                        <Skeleton variant="text" width="50%" height={24} />
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Skeleton variant="text" width={60} height={20} />
                        <Skeleton variant="text" width={60} height={20} />
                      </Box>
                    </Card>
                  ))}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
              <Card
                sx={{
                  ...cardSx,
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: { md: "center" },
                  justifyContent: "space-between",
                  gap: 2.5,
                  borderLeft: `4px solid ${NAVY}`,
                  p: 2.5,
                  mb: 3
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <Typography
                      sx={{ fontFamily: PUBLIC_SANS, fontWeight: 600, fontSize: 18, color: ON_SURFACE }}
                    >
                      Form Link
                    </Typography>
                    <Tooltip title="Click to Copy Link">
                      <IconButton size="small" onClick={() => copyFormLink()}>
                        <ContentCopy fontSize="small" sx={{ color: OUTLINE }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                      bgcolor: SURFACE_CONTAINER,
                      color: NAVY,
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      maxWidth: "100%"
                    }}
                  >
                    <LinkIcon fontSize="small" />
                    <Typography
                      sx={{
                        fontFamily: PUBLIC_SANS,
                        fontSize: 14,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {formLink}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<OpenInNew />}
                  onClick={() => openFormInNewTab()}
                  sx={{
                    bgcolor: DARK_NAVY,
                    fontFamily: PUBLIC_SANS,
                    fontWeight: 600,
                    fontSize: 13,
                    textTransform: "none",
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    boxShadow: "0px 4px 12px rgba(0, 30, 64, 0.25)",
                    "&:hover": { bgcolor: NAVY }
                  }}
                >
                  Open Form
                </Button>
              </Card>

              {/* 1. Lead Management Group */}
              <Box sx={{ mb: 4 }}>
                <SectionHeader icon={<TrendingUp />} title="Lead Management" />
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" },
                    gap: 3
                  }}
                >
                  <MetricTile
                    accent={NAVY}
                    tone={NAVY}
                    icon={<BarChart sx={{ fontSize: 24 }} />}
                    value={dashboardCount?.lead_count}
                    label="Total Lead"
                    onClick={() => navigate("/lead", { state: { city: filtereddata.city } })}
                  />
                  <MetricTile
                    accent={AMBER}
                    tone={AMBER}
                    icon={<StackedBarChart sx={{ fontSize: 24 }} />}
                    value={dashboardCount?.followup_lead_count}
                    label="Total Followup Lead"
                    onClick={() =>
                      navigate("/followup-leads", {
                        state: { city: filtereddata.city, l_phase: 3, lead_status: 3 }
                      })
                    }
                  />
                  <MetricTile
                    accent={GREEN}
                    tone={GREEN}
                    icon={<InsertChart sx={{ fontSize: 24 }} />}
                    value={dashboardCount?.confirm_lead_count}
                    label={isStaff ? "My Confirm Lead" : "Total Confirm Lead"}
                    onClick={() =>
                      navigate("/lead", {
                        state: { city: filtereddata.city, l_phase: 4, lead_status: 4 }
                      })
                    }
                  />
                  <MetricTile
                    accent={RED}
                    tone={RED}
                    icon={<Equalizer sx={{ fontSize: 24 }} />}
                    value={dashboardCount?.verification_lead_count}
                    label={
                      isStaff
                        ? "My Lead in Waiting for Verification"
                        : "Total Lead in Waiting for Verification"
                    }
                    onClick={() =>
                      navigate("/lead", {
                        state: { city: filtereddata.city, l_phase: 5, lead_status: 5 }
                      })
                    }
                  />
                </Box>
              </Box>

              {/* 2. Membership & Chapters Group */}
              <Box sx={{ mb: 4 }}>
                <SectionHeader icon={<Groups />} title="Membership & Chapters" />
                <Box
                  sx={{
                    display: "grid",
                    // 6 tracks, three equal cards: Hero stats (2) + Chapters (2) + Vacancy (2)
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "1fr 1fr",
                      md: "1fr 1fr",
                      lg: "repeat(6, 1fr)"
                    },
                    gap: 3
                  }}
                >
                  {(isAdmin || !isStaff) && (
                    <Card
                      sx={{
                        ...cardSx,
                        // Always 2 tracks: a single-track card is too narrow for a stat row
                        // and clips the label behind the number.
                        gridColumn: { lg: "span 2" },
                        p: 2.5,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center"
                      }}
                    >
                      {isAdmin && (
                        <StatRow
                          icon={<PersonSharp fontSize="small" />}
                          tint="rgba(186, 26, 26, 0.1)"
                          color={RED}
                          label="Total Members"
                          value={dashboardCount?.member_count}
                          onClick={() =>
                            navigate("/member", {
                              state: { city: filtereddata.city, activeMemberSearchType: 1 }
                            })
                          }
                        />
                      )}
                      {!isStaff && (
                        <StatRow
                          icon={<Autorenew fontSize="small" />}
                          tint="rgba(34, 197, 94, 0.1)"
                          color={GREEN}
                          label="Upcoming Renewal"
                          sub="Data before 30 days of the renewal lead insertation"
                          value={dashboardCount?.upcoming_member_renewal}
                          divider={isAdmin}
                          onClick={() =>
                            navigate("/member", {
                              state: {
                                city: filtereddata.city,
                                activeMemberSearchType: 1,
                                startDate: moment().add(31, "days").format("DD-MM-YYYY"),
                                endDate: moment().add(61, "days").format("DD-MM-YYYY"),
                                search_date_type: 1
                              }
                            })
                          }
                        />
                      )}
                    </Card>
                  )}

                  {isAdmin && (
                    <Card
                      sx={{
                        ...cardSx,
                        gridColumn: { lg: "span 2" },
                        p: 2.5,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center"
                      }}
                    >
                      <StatRow
                        icon={<Apartment fontSize="small" />}
                        tint="rgba(0, 51, 102, 0.1)"
                        color={NAVY}
                        label="Active Chapter"
                        value={dashboardCount?.active_chepter_count}
                        onClick={() =>
                          navigate("/chapter", { state: { city: filtereddata.city, ch_status: 0 } })
                        }
                      />
                      <StatRow
                        icon={<DomainDisabled fontSize="small" />}
                        tint="rgba(186, 26, 26, 0.1)"
                        color={RED}
                        label="Deactive Chapter"
                        value={dashboardCount?.deactive_chepter_count}
                        divider
                        onClick={() =>
                          navigate("/chapter", { state: { city: filtereddata.city, ch_status: 1 } })
                        }
                      />
                    </Card>
                  )}

                  {/* Vacancy counts are useful to city ambassadors too, and the numbers are
                      already scoped to their own city by the API. */}
                  {(isAdmin || isAmbassador) && (
                    <Card
                      sx={{
                        ...cardSx,
                        gridColumn: { lg: "span 2" },
                        p: 2.5,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center"
                      }}
                    >
                      <StatRow
                        icon={<PersonOff fontSize="small" />}
                        tint="rgba(180, 83, 9, 0.1)"
                        color={AMBER_DARK}
                        label="Chapters With No Coordinator"
                        sub="Nobody assigned to any role"
                        value={dashboardCount?.unassigned_chapter_count ?? 0}
                        onClick={() =>
                          navigate("/chapter", {
                            state: { city: filtereddata.city, ch_status: 0, assignment_status: 0 }
                          })
                        }
                      />
                      <StatRow
                        icon={<AssignmentInd fontSize="small" />}
                        tint="rgba(180, 83, 9, 0.1)"
                        color={AMBER_DARK}
                        label="Roles still waiting for someone"
                        sub="Chapters missing at least one role"
                        value={dashboardCount?.coordinator_vacancy_count ?? 0}
                        divider
                        onClick={() =>
                          navigate("/chapter", {
                            state: { city: filtereddata.city, ch_status: 0, assignment_status: 3 }
                          })
                        }
                      />
                    </Card>
                  )}

                  {isAmbassador && (
                    <Card
                      sx={{
                        ...cardSx,
                        gridColumn: { lg: "span 2" },
                        p: 2.5,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center"
                      }}
                    >
                      <StatRow
                        icon={<AutoStories fontSize="small" />}
                        tint="rgba(0, 51, 102, 0.1)"
                        color={NAVY}
                        label="Total Chapter"
                        value={dashboardCount?.chepter_count}
                        onClick={() => navigate("/chapter", { state: { city: filtereddata.city } })}
                      />
                      <StatRow
                        icon={<CurrencyRupeeSharp fontSize="small" />}
                        tint="rgba(34, 197, 94, 0.1)"
                        color={GREEN}
                        label="Wallet Balance"
                        value={dashboardCount?.wallet_balance}
                        divider
                        onClick={() => navigate("/wallethistory/" + loginUser.u_id, { replace: true })}
                      />
                      <StatRow
                        icon={<CurrencyRupeeSharp fontSize="small" />}
                        tint="rgba(180, 83, 9, 0.1)"
                        color={AMBER_DARK}
                        label="Payable Wallet"
                        value={dashboardCount?.stock_pending_balance}
                        divider
                        onClick={() => navigate("/citywallethistory", { state: { city: filtereddata.city } })}
                      />
                    </Card>
                  )}

                  {isStaff && (
                    <MetricTile
                      accent={RED}
                      tone={RED}
                      icon={<LeaderboardSharp sx={{ fontSize: 24 }} />}
                      value={dashboardCount?.lead_allocated_count}
                      label="My Lead"
                    />
                  )}
                </Box>
              </Box>

              {/* 2b. Receivable Stock — City Side (Pending Stock Management) — hidden when 0 */}
              {(loginUser?.u_type == 1 || (loginUser?.u_type == 2 && loginUser?.p_user_id != 1)) &&
                Number(dashboardCount?.receivable_stock_count || 0) > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <SectionHeader icon={<Inventory2Outlined />} title="Stock Receivable" />
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" },
                        gap: 3
                      }}
                    >
                      <Tooltip
                        title={
                          dashboardCount?.receivable_stock_details?.length
                            ? dashboardCount.receivable_stock_details.map((d) => `${d.p_name}: ${d.qty} (${d.date})`).join("\n")
                            : "No pending stock"
                        }
                        arrow
                      >
                        <Box>
                          <MetricTile
                            accent={dashboardCount?.receivable_stock_count > 0 ? AMBER : NAVY}
                            tone={dashboardCount?.receivable_stock_count > 0 ? AMBER : NAVY}
                            icon={<Inventory2Outlined sx={{ fontSize: 24 }} />}
                            value={dashboardCount?.receivable_stock_count ?? 0}
                            label="Receivable Stock"
                            onClick={() => navigate("/products", { state: { filterPending: true } })}
                          />
                        </Box>
                      </Tooltip>
                      {/* Show details inline if needed */}
                      {dashboardCount?.receivable_stock_details?.length > 0 && (
                        <Card
                          sx={{
                            ...cardSx,
                            p: 2,
                            gridColumn: { xs: "span 1", lg: "span 3" },
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-start",
                            alignSelf: "stretch"
                          }}
                        >
                          <Typography sx={{ fontFamily: PUBLIC_SANS, fontWeight: 700, fontSize: 13, color: SECONDARY_TEXT, mb: 1.2 }}>
                            Pending Breakdown
                          </Typography>
                          <Stack direction="row" flexWrap="wrap" gap={0.8}>
                            {dashboardCount.receivable_stock_details.slice(0, 8).map((d) => (
                              <Chip
                                key={d.p_id + d.date}
                                label={`${d.p_name}: ${d.qty} (${d.date})`}
                                size="small"
                                onClick={() =>
                                  navigate("/products", {
                                    state: {
                                      autoOpenHistory: true,
                                      p_id: d.p_id,
                                      p_name: d.p_name,
                                      p_image: d.p_image,
                                      p_type: d.p_type
                                    }
                                  })
                                }
                                sx={{
                                  bgcolor: "#fff",
                                  border: "1px solid #fde68a",
                                  fontWeight: 600,
                                  fontSize: "0.72rem",
                                  height: 24,
                                  cursor: "pointer",
                                  "&:hover": { bgcolor: "#fffbeb", borderColor: "#f59e0b" }
                                }}
                              />
                            ))}
                            {dashboardCount.receivable_stock_details.length > 8 && (
                              <Chip
                                label={`+${dashboardCount.receivable_stock_details.length - 8} more`}
                                size="small"
                                onClick={() => navigate("/products", { state: { filterPending: true } })}
                                sx={{ bgcolor: "#f8fafc", fontSize: "0.72rem", height: 24, cursor: "pointer", border: "1px solid #e2e8f0" }}
                              />
                            )}
                          </Stack>
                        </Card>
                      )}
                    </Box>
                    {dashboardCount?.receivable_stock_count > 0 && (
                      <Alert severity="warning" sx={{ mt: 2, bgcolor: "#FFFBEB", border: "1px solid #fde68a", color: "#92400e" }}>
                        You have <strong>{dashboardCount.receivable_stock_count}</strong> item
                        {dashboardCount.receivable_stock_count !== 1 ? "s" : ""} pending acceptance. Click the tile to view and accept stock — yellow highlighted cards await your approval.
                      </Alert>
                    )}
                  </Box>
                )}

              {/* 3. Request Approvals + Pending Members Queue */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", lg: showApprovals ? "1fr 1fr" : "1fr" },
                  gap: 3,
                  mb: 4
                }}
              >
                {showApprovals && (
                  <Box>
                    <SectionHeader icon={<FactCheck />} title="Request Approvals" />
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" },
                        gap: 3
                      }}
                    >
                      <ApprovalTile
                        tone={ORANGE}
                        imageSrc="/static/icons/verified_orange.png"
                        title="OBT Requests"
                        today={dashboardCount?.today_obt}
                        pending={dashboardCount?.pending_obt}
                        onClick={() => navigate("/obt-apply", { state: { city: filtereddata.city } })}
                      />
                      <ApprovalTile
                        tone={GREEN}
                        imageSrc="/static/icons/verified.png"
                        title="Green Tick"
                        today={dashboardCount?.today_v2v}
                        pending={dashboardCount?.pending_v2v}
                        onClick={() => navigate("/v2vlist", { state: { city: filtereddata.city } })}
                      />
                      {isAdmin && (
                        <ApprovalTile
                          tone={BLUE}
                          imageSrc="/static/icons/verified_blue.png"
                          title="MGT Requests"
                          today={dashboardCount?.today_mgt}
                          pending={dashboardCount?.pending_mgt}
                          onClick={() =>
                            navigate("/bluetick-apply", { state: { city: filtereddata.city } })
                          }
                        />
                      )}
                    </Box>
                  </Box>
                )}
                <Box>
                  <SectionHeader icon={<Queue />} title="Pending Members Queue" />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" },
                      gap: 3
                    }}
                  >
                    <QueueCard
                      tone={ORANGE}
                      imageSrc="/static/icons/verified_orange.png"
                      value={dashboardCount?.pending_obt_member}
                      label="Pending OBT Members"
                      onClick={() =>
                        navigate("/member", {
                          replace: true,
                          state: {
                            activeMemberTickType: [5],
                            activeMemberSearchType: 1,
                            city: filtereddata.city
                          }
                        })
                      }
                    />
                    <QueueCard
                      tone={GREEN}
                      imageSrc="/static/icons/verified.png"
                      value={dashboardCount?.pending_green_tick_member}
                      label="Pending Green Tick Members"
                      onClick={() =>
                        navigate("/member", {
                          replace: true,
                          state: {
                            activeMemberTickType: [6],
                            activeMemberSearchType: 1,
                            city: filtereddata.city
                          }
                        })
                      }
                    />
                    <QueueCard
                      tone={BLUE}
                      imageSrc="/static/icons/verified_blue.png"
                      value={dashboardCount?.pending_blue_tick_member}
                      label="Pending Blue Tick (MGT) Members"
                      onClick={() =>
                        navigate("/member", {
                          replace: true,
                          state: {
                            activeMemberTickType: [7],
                            activeMemberSearchType: 1,
                            city: filtereddata.city
                          }
                        })
                      }
                    />
                  </Box>
                </Box>
              </Box>

              {/* 4. Pin Wise Eligible Members */}
              {dashboardCount?.pin_eligible_member_counter?.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <SectionHeader icon={<EmojiEvents />} title="Pin Wise Eligible Members" />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "1fr 1fr",
                        md: "1fr 1fr 1fr",
                        lg: "repeat(auto-fill, minmax(180px, 1fr))"
                      },
                      gap: 3
                    }}
                  >
                    {dashboardCount.pin_eligible_member_counter.map((pin) => (
                      <PinEligibleCard
                        key={pin.p_a_p_id}
                        imageUrl={pin.image_url}
                        name={pin.name}
                        counter={pin.counter}
                        onClick={() =>
                          navigate("/pininmemberlist", {
                            state: {
                              p_id: pin.p_id,
                              p_type: 1,
                              p_a_p_id: pin.p_a_p_id,
                              p_name: pin.name,
                              image: pin.image,
                              image_url: pin.image_url,
                              city_id: filtereddata.city
                            }
                          })
                        }
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* 5. Suggestions & Feedback */}
              {(showApprovals || showFeedback || !isStaff) && (
                <Box sx={{ mb: 4 }}>
                  <SectionHeader icon={<RateReviewOutlined />} title="Activity Overview" />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" },
                      gap: 3
                    }}
                  >
                    {!isStaff && (
                      <MeetingStatsCard
                        meetingToday={dashboardCount?.today_meeting}
                        meetingClosePending={dashboardCount?.pending_meeting_close}
                        onClick={() => navigate("/meeting", { state: { city: filtereddata.city } })}
                      />
                    )}
                    {showApprovals && (
                      <FeedbackTile
                        tone={NAVY}
                        icon={<Lightbulb sx={{ fontSize: 22 }} />}
                        title="Suggestions"
                        today={dashboardCount?.today_suggestion}
                        pending={dashboardCount?.pedning_suggestion}
                        onClick={() =>
                          navigate("/lvb-suggestion", { state: { city: filtereddata.city } })
                        }
                      />
                    )}
                    {showApprovals && (
                      <FeedbackTile
                        tone={NAVY}
                        icon={<ThumbUp sx={{ fontSize: 22 }} />}
                        title="Thank You Note"
                        today={dashboardCount?.today_thankyounote}
                        pending={dashboardCount?.pedning_thankyounote}
                        onClick={() =>
                          navigate("/business-approval", { state: { city: filtereddata.city } })
                        }
                      />
                    )}
                    {showFeedback && (
                      <Card
                        onClick={() =>
                          navigate("/meeting-feedback-report", { state: { city: filtereddata.city, mf_read: 0 }, replace: true })
                        }
                        sx={{
                          ...cardSx,
                          p: 2.5,
                          display: "flex",
                          flexDirection: "column",
                          cursor: "pointer"
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                          <Box sx={{ color: YELLOW, display: "flex", alignItems: "center" }}>
                            <Chat sx={{ fontSize: 22 }} />
                          </Box>
                          <Typography
                            sx={{ fontFamily: PUBLIC_SANS, fontWeight: 700, fontSize: 13, color: SECONDARY_TEXT }}
                          >
                            Unread Meeting Feedback
                          </Typography>
                        </Stack>
                        <Box sx={{ mt: "auto" }}>
                          <Typography
                            sx={{
                              fontFamily: PUBLIC_SANS,
                              fontWeight: 800,
                              fontSize: 28,
                              lineHeight: 1,
                              color: ON_SURFACE
                            }}
                          >
                            {dashboardCount?.pending_feedback}
                          </Typography>
                        </Box>
                      </Card>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Container>
      </Box>
    </Page>
  );
}
